// src/lib/archiveConfirm.js — one confirm for archiving anything.
//
// P0.31 task 31.B.6. Six entities soft-delete and each screen had written (or
// not written) its own wording. That is how the product ended up with two
// archive MECHANISMS, and the copy was drifting the same way.
//
// THE PRECEDENT IS THE DISCONNECT DIALOG (ContactsTab.jsx, 16 Oct). It works
// because it states what the action does NOT do — "The contact stays in your
// list and nothing is deleted" — and then names what changes. A delete dialog
// has to be equally specific in the other direction, and the honest sentence is
// that nothing is destroyed and the row can be brought back.
//
// WHY `window.confirm` AND NOT A MODAL COMPONENT. The Disconnect dialog is a
// window.confirm, every existing delete on ContactsTab and StrategicSignalsTab
// is a window.confirm, and this phase is about making six surfaces agree rather
// than introducing a seventh pattern. A modal is a reasonable future change —
// it would be ONE change here instead of six.

/**
 * Ask before archiving. Returns true if the user confirmed.
 *
 * @param {object}  o
 * @param {string}  o.entity   what is being archived, e.g. 'contact', 'account'
 * @param {string}  [o.name]   its name, shown in the question
 * @param {string}  [o.keeps]  one sentence naming what is NOT affected. Omit and
 *                             the generic promise is used.
 * @param {string}  [o.restoreFrom]  where to find it again. Defaults to the
 *                             Archived view, which every page now has.
 */
export function confirmArchive({ entity, name, keeps, restoreFrom } = {}) {
  const what = name ? `“${name}”` : `this ${entity || 'item'}`;
  const where = restoreFrom || 'the Archived view on this page';
  return window.confirm(
    `Archive ${what}?\n\n`
    + `It is hidden from your lists. `
    + (keeps ? keeps + ' ' : 'Nothing is deleted. ')
    + `You can restore it from ${where}.`
  );
}

/**
 * Ask before archiving something that still has children — used when the API
 * has refused with a count and the caller may retry with `force`.
 *
 * Separate from confirmArchive on purpose: this is a DIFFERENT question. The
 * first asks "are you sure"; this one asks "are you sure, knowing these N things
 * will be left pointing at something you can no longer open". Collapsing them
 * would hide the second question inside the first.
 */
export function confirmArchiveWithChildren({ entity, name, blockedBy } = {}) {
  const what = name ? `“${name}”` : `this ${entity || 'item'}`;
  const parts = [];
  if (blockedBy?.contacts)      parts.push(`${blockedBy.contacts} contact${blockedBy.contacts === 1 ? '' : 's'}`);
  if (blockedBy?.opportunities) parts.push(`${blockedBy.opportunities} open opportunit${blockedBy.opportunities === 1 ? 'y' : 'ies'}`);
  const list = parts.join(' and ') || 'other records';
  return window.confirm(
    `${what} still has ${list}.\n\n`
    + `Archiving it anyway leaves ${parts.length > 1 ? 'them' : 'them'} in place, `
    + `pointing at an account you will no longer be able to open.\n\n`
    + `Archive anyway?`
  );
}
